import type { ReactNode } from 'react';
import { Box, Typography, type TypographyProps } from '@mui/material';
import { SKIN_CONFIGS } from '../../config/skinRegistry';
import { useSkinConfigs } from '../../hooks/useSkinConfigs';
import {
  AMOUNT_LABEL_GAP,
  formattedAmountStyles as styles,
  getFormattedAmountLayoutSx,
  type FormattedAmountLayout,
} from '../../styles/components/formattedAmount';
import { mergeSx } from '../../utils/styles';
import { CurrencySymbol } from './CurrencySymbol';

export type FormattedAmountProps = TypographyProps & {
  value: ReactNode;
  currency?: string;
  unit?: string;
  layout?: FormattedAmountLayout;
  currencyProps?: TypographyProps;
  label?: ReactNode;
};

/** Displays an already-formatted value with the skin currency label or an opaque asset/network unit. */
export const FormattedAmount = ({
  value,
  currency,
  unit,
  label,
  currencyProps,
  layout = 'inline',
  ...typographyProps
}: FormattedAmountProps) => {
  const { showCurrencyAsIso, currencyPosition } = useSkinConfigs(SKIN_CONFIGS);

  const labelPosition = unit === undefined ? currencyPosition : 'right';
  const isTruncated = typographyProps.noWrap === true && layout === 'inline';
  const isInlineUnit = unit !== undefined && layout === 'inline' && !isTruncated;

  const labelProps = {
    variant: 'inherit',
    ...currencyProps,
    sx: mergeSx(currencyProps?.sx, isTruncated && labelPosition === 'right' ? { ml: AMOUNT_LABEL_GAP } : undefined),
  } as const;
  let amountLabel = label;

  if (amountLabel === undefined) {
    if (unit !== undefined || showCurrencyAsIso === true) {
      amountLabel = (
        <Typography component='span' {...labelProps}>
          {unit ?? currency}
        </Typography>
      );
    } else {
      amountLabel = <CurrencySymbol currency={currency} {...labelProps} />;
    }
  }

  return (
    <Typography component='span' variant='inherit' {...typographyProps}>
      <Box
        component='span'
        sx={mergeSx(getFormattedAmountLayoutSx(layout, labelPosition, isTruncated), isInlineUnit && styles.inlineUnit)}
      >
        {labelPosition === 'left' && amountLabel}
        <Box component='span'>{value}</Box>
        {labelPosition === 'right' && amountLabel}
      </Box>
    </Typography>
  );
};
